import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Menu,
  X,
  MapPin,
  Phone,
  Mail,
  Facebook,
  Youtube,
  Instagram,
  ArrowRight,
  ArrowUp,
  Newspaper,
  Video,
  Play,
  ExternalLink,
  Users,
  HeartHandshake,
  Sprout,
  GraduationCap,
  Stethoscope,
  HandHeart,
  Landmark,
  Megaphone,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import {
  PROFILE,
  VALUES,
  STATS,
  BIOGRAPHY,
  FOCUS_AREAS,
  POSITIONS,
  MILESTONES,
  NEWS_CLIPPINGS,
  FB_VIDEOS,
  YOUTUBE,
  YOUTUBE_VIDEOS,
  GALLERY,
  MALAYALAM,
} from "./data";

/* Party affiliation label shown in the header/footer lockup */
const PARTY = "Indian National Congress";

/* Map string names from data.js to actual icon components */
const ICONS = {
  Users,
  HeartHandshake,
  Sprout,
  GraduationCap,
  Stethoscope,
  HandHeart,
  Landmark,
  Megaphone,
};

const NAV_LINKS = [
  { name: "About", id: "about" },
  { name: "Positions", id: "positions" },
  { name: "Service", id: "work" },
  { name: "Journey", id: "journey" },
  { name: "Press", id: "news" },
  { name: "Watch", id: "videos" },
  { name: "Contact", id: "contact" },
];

/* Branding derived from PROFILE.name so it updates if the name changes */
const NAME_PARTS = PROFILE.name.trim().split(/\s+/);
const BRAND_TOP = NAME_PARTS[0];
const BRAND_BOTTOM = NAME_PARTS.slice(1).join(" ") || NAME_PARTS[0];

const scrollTo = (id) =>
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

/* Shared reveal — transform/opacity only, runs once (no scroll-time repaint) */
const reveal = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 0.61, 0.36, 1] },
  },
};
const revealView = {
  initial: "hidden",
  whileInView: "show",
  viewport: { once: true, margin: "-70px" },
  variants: reveal,
};

/* rAF-throttled scroll flag — avoids a state update on every scroll frame */
const useScrolled = (threshold = 24) => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        setScrolled(window.scrollY > threshold);
        raf = 0;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [threshold]);
  return scrolled;
};

/* True masonry via CSS Grid: a tiny grid-auto-rows unit + a measured
   row-span per item lets tiles keep their natural photo aspect ratio
   (no cropping) while still packing tightly with no leftover gaps —
   including "featured" tiles that span extra columns. */
const useMasonry = () => {
  const gridRef = useRef(null);
  useLayoutEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;

    const layout = () => {
      const styles = getComputedStyle(grid);
      const rowHeight = parseFloat(styles.gridAutoRows) || 1;
      const rowGap = parseFloat(styles.rowGap) || 0;
      [...grid.children].forEach((item) => {
        // scrollHeight (not getBoundingClientRect) — the item's own box is
        // already clipped to the small grid-auto-rows track before a span
        // is assigned, but scrollHeight still reports the true content size.
        const contentHeight = item.scrollHeight;
        const span = Math.ceil((contentHeight + rowGap) / (rowHeight + rowGap));
        item.style.gridRowEnd = `span ${span}`;
      });
    };

    // Run once synchronously so the first paint is already close (covers
    // any images already cached/loaded).
    layout();

    // Photos load asynchronously, so their real height isn't known yet on
    // that first pass — relisten and relayout as each one finishes. Image
    // "load" events fire regardless of tab visibility, unlike rAF/
    // ResizeObserver, which some environments throttle on background tabs.
    const imgs = [...grid.querySelectorAll("img")];
    imgs.forEach((img) => {
      if (!img.complete) {
        img.addEventListener("load", layout);
        img.addEventListener("error", layout);
      }
    });

    // Best-effort relayout for window resizes / responsive column changes.
    let raf = 0;
    const schedule = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(layout);
    };
    const ro = new ResizeObserver(schedule);
    ro.observe(grid);

    return () => {
      imgs.forEach((img) => {
        img.removeEventListener("load", layout);
        img.removeEventListener("error", layout);
      });
      ro.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);
  return gridRef;
};

/* Small reusable section kicker (optional Malayalam companion word) */
const Kicker = ({ children, center, icon: Icon, ml }) => (
  <span className={`sn-eyebrow ${center ? "center" : ""}`}>
    {Icon && <Icon className="w-3.5 h-3.5" />}
    {children}
    {ml && (
      <span className="font-mal text-saffron normal-case tracking-normal">
        · {ml}
      </span>
    )}
  </span>
);

/* Name + party lockup used in header and footer */
const BrandLockup = ({ onClick, tone = "light" }) => (
  <button onClick={onClick} className="flex items-center gap-3 text-left">
    <span className="sn-logo-badge">
      <img
        src={PROFILE.logo}
        alt={`${PROFILE.name} logo`}
        className="sn-logo-badge__img"
      />
    </span>
    <span className="leading-tight">
      <span
        className={`block sn-nameplate text-lg ${
          tone === "light" ? "text-ink" : "text-white"
        }`}
      >
        {PROFILE.name}
      </span>
      <span
        className={`block sn-label mt-1 ${
          tone === "light" ? "!text-green-deep" : "!text-saffron-light"
        }`}
      >
        {PARTY}
      </span>
    </span>
  </button>
);

/* ===================================================================== */
/* Header — tricolour bar + masthead                                     */
/* ===================================================================== */
const Header = () => {
  const scrolled = useScrolled(24);
  const [open, setOpen] = useState(false);

  const go = (id) => {
    setOpen(false);
    scrollTo(id);
  };

  return (
    <header className={`sn-header ${scrolled ? "is-scrolled" : ""}`}>
      <div className="sn-tricolour" />
      <div className="sn-header__bar">
        <div className="sn-container flex items-center justify-between w-full">
          <BrandLockup onClick={() => go("home")} />

          <nav className="hidden md:flex items-center gap-7">
            {NAV_LINKS.map((l) => (
              <button key={l.id} onClick={() => go(l.id)} className="sn-navlink">
                {l.name}
              </button>
            ))}
            <a
              href={PROFILE.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="sn-btn sn-btn-primary !py-2 !px-3.5"
            >
              <Facebook className="w-3.5 h-3.5" /> Follow
            </a>
          </nav>

          <button
            className="md:hidden text-ink p-2"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="md:hidden absolute top-full inset-x-0 bg-paper border-b border-line shadow-soft"
          >
            <div className="sn-container py-3 flex flex-col">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.id}
                  onClick={() => go(l.id)}
                  className="sn-navlink !text-sm py-3 text-left border-b border-line last:border-0"
                >
                  {l.name}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

/* ===================================================================== */
/* Hero                                                                   */
/* ===================================================================== */
const Hero = () => (
  <section id="home" className="pt-28 md:pt-32 pb-8">
    <div className="sn-container grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
      {/* Lead */}
      <motion.div
        initial="hidden"
        animate="show"
        variants={{ show: { transition: { staggerChildren: 0.1 } } }}
        className="lg:col-span-7"
      >
        <motion.span
          variants={reveal}
          className="block font-mal text-saffron text-sm mb-3"
        >
          {MALAYALAM.greeting}
        </motion.span>

        <motion.span variants={reveal}>
          <Kicker>Koithoorkonam · Kerala</Kicker>
        </motion.span>

        <motion.h1
          variants={reveal}
          className="sn-nameplate mt-5 mb-4"
          style={{ fontSize: "clamp(2.7rem, 8.5vw, 6rem)" }}
        >
          <span className="sn-name-tricolour">{BRAND_TOP}</span>
          <br />
          <span className="sn-name-tricolour">{BRAND_BOTTOM}</span>
        </motion.h1>

        <motion.div
          variants={reveal}
          className="flex items-center gap-3 flex-wrap mb-5"
        >
          <span className="sn-tricolour-chip" />
          <span className="font-mal text-green text-lg md:text-xl leading-snug">
            {MALAYALAM.tagline}
          </span>
        </motion.div>

        <motion.p
          variants={reveal}
          className="sn-byline !text-ink-soft mb-5"
        >
          {PROFILE.title}
        </motion.p>

        <motion.p variants={reveal} className="sn-lead max-w-xl mb-6">
          {PROFILE.heroIntro}
        </motion.p>

        <motion.div
          variants={reveal}
          className="flex items-center gap-2 text-muted mb-8 sn-label"
        >
          <MapPin className="w-4 h-4 text-saffron" />
          <span>{PROFILE.location}</span>
        </motion.div>

        <motion.div variants={reveal} className="flex flex-wrap gap-3">
          <button
            onClick={() => scrollTo("work")}
            className="sn-btn sn-btn-primary"
          >
            His Work <ArrowRight className="w-4 h-4" />
          </button>
          <button
            onClick={() => scrollTo("contact")}
            className="sn-btn sn-btn-outline"
          >
            <Phone className="w-4 h-4" /> Get in touch
          </button>
        </motion.div>
      </motion.div>

      {/* Portrait */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 0.61, 0.36, 1] }}
        className="lg:col-span-5"
      >
        <figure className="sn-figure">
          <img
            src={PROFILE.photo}
            alt={PROFILE.name}
            width="380"
            height="480"
            className="h-[22rem] sm:h-[26rem] w-full object-cover object-top"
          />
          <figcaption className="sn-figure__plate">
            <span className="font-mal normal-case tracking-normal text-[0.72rem]">
              {MALAYALAM.photoPlate}
            </span>
            <span>Kerala</span>
          </figcaption>
        </figure>
        <p className="text-lg text-ink italic leading-snug mt-5 border-l-2 border-green pl-4">
          “{PROFILE.tagline}”
        </p>
      </motion.div>
    </div>
  </section>
);

/* ===================================================================== */
/* Values ticker                                                          */
/* ===================================================================== */
const Marquee = () => {
  const loop = [...VALUES, ...VALUES];
  return (
    <div className="sn-marquee my-6" aria-hidden="true">
      <span className="sn-marquee__label">Principles</span>
      <div className="sn-marquee__viewport">
        <div className="sn-marquee__track">
          {loop.map((v, i) => (
            <span key={i} className="sn-marquee__item">
              {v}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ===================================================================== */
/* Stats — by the numbers                                                 */
/* ===================================================================== */
const Stats = () => (
  <section className="pt-6 pb-16">
    <div className="sn-container">
      <div className="flex items-center gap-4 mb-6">
        <Kicker ml={MALAYALAM.sections.stats}>By the Numbers</Kicker>
        <hr className="sn-rule flex-1" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 border-t-2 border-b-2 border-green divide-y sm:divide-y-0 divide-line md:divide-x md:divide-line">
        {STATS.map((s, i) => (
          <motion.div key={i} {...revealView} className="text-center px-4 py-7">
            <div className="sn-stat__value text-4xl md:text-5xl">{s.value}</div>
            <div className="sn-stat__label mt-2.5">{s.label}</div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

/* ===================================================================== */
/* About                                                                  */
/* ===================================================================== */
const About = () => (
  <section id="about" className="sn-section">
    <div className="sn-container">
      <div className="flex items-center gap-4 mb-12">
        <Kicker ml={MALAYALAM.sections.about}>About</Kicker>
        <hr className="sn-rule flex-1" />
      </div>

      <div className="grid md:grid-cols-12 gap-10 lg:gap-14 items-start">
        <motion.div {...revealView} className="md:col-span-5">
          <figure className="sn-figure">
            <img
              src={PROFILE.aboutPhoto}
              alt={PROFILE.name}
              className="h-[24rem] w-full object-cover object-top"
            />
            <figcaption className="sn-figure__plate">
              <span>Portrait</span>
              <span>{PARTY}</span>
            </figcaption>
          </figure>
          <blockquote className="mt-6 border-l-2 border-green pl-5">
            {/* His own words, in Malayalam — "I am Koithoorkanam Sundaran" */}
            <span className="block font-mal text-green-deep text-xl md:text-2xl leading-snug">
              {MALAYALAM.selfIntro}
            </span>
            <span className="block text-ink-soft italic text-base mt-2">
              “I am Koithoorkanam Sundaran.”
            </span>
          </blockquote>
        </motion.div>

        <motion.div {...revealView} className="md:col-span-7">
          <h2 className="sn-title mb-7">
            A life dedicated to <span className="accent">public service</span>
          </h2>
          {BIOGRAPHY.map((para, i) => (
            <p
              key={i}
              className={`text-ink-soft mb-5 leading-relaxed ${
                i === 0 ? "sn-dropcap" : ""
              }`}
            >
              {para}
            </p>
          ))}
          <p className="sn-byline mt-6 mb-7">— {PROFILE.name}</p>
          <div className="flex flex-wrap gap-3">
            <a
              href={PROFILE.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="sn-btn sn-btn-outline"
            >
              <Facebook className="w-4 h-4" /> Facebook
            </a>
            <button
              onClick={() => scrollTo("journey")}
              className="sn-btn sn-btn-outline"
            >
              His Journey
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

/* ===================================================================== */
/* Positions held — the ledger of responsibilities (പദവികൾ)              */
/* ===================================================================== */
const Positions = () => (
  <section id="positions" className="sn-section sn-dark">
    <div className="sn-container">
      <motion.div {...revealView} className="max-w-2xl mb-10">
        <Kicker icon={Landmark} ml={MALAYALAM.sections.positions}>
          Positions Held
        </Kicker>
        <h2 className="sn-title mt-4">
          Responsibilities in the <span className="accent">movement</span>
        </h2>
      </motion.div>

      <div className="border-t border-paper/15">
        {POSITIONS.map((p, i) => (
          <motion.div
            key={i}
            {...revealView}
            className="grid md:grid-cols-12 gap-1.5 md:gap-6 items-baseline py-5 border-b border-paper/15"
          >
            <span className="hidden md:block md:col-span-1 font-display font-extrabold text-saffron-light text-sm pt-1">
              {String(i + 1).padStart(2, "0")}
            </span>
            <div className="md:col-span-6">
              <h3 className="font-display text-lg md:text-xl font-bold flex flex-wrap items-center gap-2.5">
                {p.role}
                {p.current && (
                  <span className="inline-flex items-center gap-1.5 bg-saffron text-white text-[0.6rem] font-sans font-bold tracking-widest uppercase px-2 py-0.5 rounded">
                    <span className="font-mal normal-case tracking-normal">
                      {MALAYALAM.current}
                    </span>
                    · Current
                  </span>
                )}
              </h3>
              <p className="text-paper/75 text-sm mt-0.5">
                {p.org}
                {p.note && (
                  <span className="text-paper/50"> — {p.note}</span>
                )}
              </p>
            </div>
            {/* മലയാളത്തിൽ — the same position in Malayalam */}
            <p className="md:col-span-5 md:text-right font-mal text-saffron-light/90 text-sm leading-relaxed">
              {p.ml}
            </p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

/* ===================================================================== */
/* Areas of Service                                                       */
/* ===================================================================== */
const Work = () => (
  <section id="work" className="sn-section sn-bg-paper2">
    <div className="sn-container">
      <motion.div
        {...revealView}
        className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-8"
      >
        <div className="max-w-2xl">
          <Kicker ml={MALAYALAM.sections.work}>Areas of Service</Kicker>
          <h2 className="sn-title mt-4">
            The work he <span className="accent">stands for</span>
          </h2>
        </div>
        <p className="sn-lead max-w-sm">
          The causes and everyday work that define his contribution to the
          community.
        </p>
      </motion.div>

      <div className="grid md:grid-cols-2 md:gap-x-14 border-b border-line">
        {FOCUS_AREAS.map((f, i) => {
          const Icon = ICONS[f.icon] || HeartHandshake;
          return (
            <motion.div key={i} {...revealView} className="sn-service">
              <span className="sn-service__num">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="sn-service__icon">
                <Icon className="w-6 h-6" />
              </span>
              <div>
                <h3 className="font-display text-xl font-bold mb-1.5">
                  {f.title}
                </h3>
                <p className="text-ink-soft text-sm leading-relaxed">
                  {f.description}
                </p>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);

/* ===================================================================== */
/* Journey — timeline                                                     */
/* ===================================================================== */
const Journey = () => (
  <section id="journey" className="sn-section">
    <div className="sn-container">
      <motion.div {...revealView} className="max-w-2xl mb-12">
        <Kicker ml={MALAYALAM.sections.journey}>His Journey</Kicker>
        <h2 className="sn-title mt-4">
          The <span className="accent">journey</span> so far
        </h2>
      </motion.div>

      <div className="max-w-3xl">
        {MILESTONES.map((m, i) => {
          const last = i === MILESTONES.length - 1;
          return (
            <motion.div key={i} {...revealView} className="flex gap-5 md:gap-8">
              <div className="flex flex-col items-center pt-1.5">
                <span className="w-3.5 h-3.5 rounded-full bg-paper border-2 border-green shrink-0" />
                {!last && (
                  <span className="w-px flex-1 my-1 bg-gradient-to-b from-green/70 to-saffron/40" />
                )}
              </div>
              <div className={`pb-10 ${last ? "!pb-0" : ""} flex-1`}>
                <span className="inline-block font-display font-bold text-sm tracking-wider text-white bg-green px-2.5 py-1 rounded">
                  {m.year}
                </span>
                <h3 className="font-display text-xl font-bold mt-3 mb-2">
                  {m.title}
                </h3>
                <p className="text-ink-soft text-sm leading-relaxed max-w-xl">
                  {m.text}
                </p>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);

/* ===================================================================== */
/* News cutouts — archive + lightbox                                      */
/* ===================================================================== */
const CLIP_ROTATE = [-1.5, 1.2, -1, 1.4];

const News = () => {
  const [active, setActive] = useState(null);

  return (
    <section id="news" className="sn-section sn-bg-paper2">
      <div className="sn-container">
        <motion.div {...revealView} className="text-center max-w-2xl mx-auto mb-12">
          <Kicker center icon={Newspaper} ml={MALAYALAM.sections.news}>
            In the Press
          </Kicker>
          <h2 className="sn-title mt-4">
            News <span className="accent">clippings</span>
          </h2>
          <p className="sn-lead mt-4">
            Coverage of his work in newspapers and local media. Click a clipping
            to view it larger.
          </p>
        </motion.div>

        <div className="sn-clip-grid">
          {NEWS_CLIPPINGS.map((c, i) => (
            <motion.div key={i} {...revealView}>
              <button
                onClick={() => setActive(c)}
                data-stamp={`Filed ${c.date}`}
                className="sn-clip text-left w-full rounded-sm"
                style={{ transform: `rotate(${CLIP_ROTATE[i % 4]}deg)` }}
              >
                <div className="overflow-hidden rounded-t-sm">
                  <img
                    src={c.image}
                    alt={c.title}
                    className="w-full h-48 md:h-52 object-cover object-top border-b border-line"
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-display font-bold text-ink text-base leading-snug mb-2">
                    {c.title}
                  </h3>
                  <p className="sn-label">
                    {c.source} · {c.date}
                  </p>
                </div>
              </button>
            </motion.div>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            className="sn-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
          >
            <motion.div
              initial={{ scale: 0.94, y: 16 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.94, y: 16 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-card border border-line rounded-sm overflow-hidden max-w-2xl w-full max-h-[90vh] flex flex-col"
            >
              <div className="flex items-center justify-between p-4 border-b border-line">
                <div>
                  <h3 className="font-display font-bold text-ink">
                    {active.title}
                  </h3>
                  <p className="sn-label mt-1">
                    {active.source} · {active.date}
                  </p>
                </div>
                <button
                  onClick={() => setActive(null)}
                  className="text-ink hover:text-green p-1"
                  aria-label="Close"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="overflow-auto p-4 bg-paper-2">
                <img
                  src={active.image}
                  alt={active.title}
                  className="w-full border border-line"
                />
              </div>
              {active.link && (
                <a
                  href={active.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sn-btn sn-btn-outline m-4 justify-center"
                >
                  Read full article <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

/* ===================================================================== */
/* Video plate grid — shared by Facebook + YouTube                        */
/* ===================================================================== */
const VideoPlate = ({ children, caption, icon: Icon }) => (
  <div className="sn-plate rounded-sm overflow-hidden">
    <div className="aspect-video bg-paper-2 border-b border-line flex items-center justify-center">
      {children}
    </div>
    <div className="p-4 flex items-center gap-2">
      <Icon className="w-4 h-4 text-green shrink-0" />
      <p className="text-ink text-sm font-medium">{caption}</p>
    </div>
  </div>
);

const PlaceholderCard = ({ href, label }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="flex flex-col items-center justify-center text-center p-6 w-full h-full hover:bg-paper transition-colors"
  >
    <span className="w-14 h-14 rounded-full border-2 border-green flex items-center justify-center mb-3">
      <Play className="w-6 h-6 text-green ml-1" />
    </span>
    <span className="text-muted text-sm">{label}</span>
  </a>
);

/* ===================================================================== */
/* Facebook videos                                                        */
/* ===================================================================== */
/* Video permalinks (/videos/, /reel/, /watch) use the Video plugin;
   everything else (story.php, photo.php, permalink.php posts) needs the
   Post plugin instead, or Facebook renders "Content Currently Unavailable". */
const fbEmbedSrc = (url) => {
  const isVideoUrl = /\/(videos|reel|watch)\b/.test(url);
  const plugin = isVideoUrl ? "video.php" : "post.php";
  return `https://www.facebook.com/plugins/${plugin}?href=${encodeURIComponent(
    url
  )}&show_text=false&width=560&t=0`;
};

const Videos = () => (
  <section id="videos" className="sn-section">
    <div className="sn-container">
      <motion.div {...revealView} className="text-center max-w-2xl mx-auto mb-12">
        <Kicker center icon={Video} ml={MALAYALAM.sections.videos}>
          Watch · Facebook
        </Kicker>
        <h2 className="sn-title mt-4">
          Video from his <span className="accent">Facebook</span>
        </h2>
        <p className="sn-lead mt-4">
          Moments, speeches and on-the-ground work shared on his official
          Facebook page.
        </p>
      </motion.div>

      <div className="grid md:grid-cols-3 gap-8">
        {FB_VIDEOS.map((v, i) => (
          <motion.div key={i} {...revealView}>
            <VideoPlate caption={v.caption} icon={Facebook}>
              {v.url ? (
                <iframe
                  title={v.caption || `Facebook video ${i + 1}`}
                  src={fbEmbedSrc(v.url)}
                  className="w-full h-full"
                  style={{ border: "none", overflow: "hidden" }}
                  scrolling="no"
                  frameBorder="0"
                  allowFullScreen
                  allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                />
              ) : (
                <PlaceholderCard
                  href={PROFILE.facebook}
                  label="Add a Facebook video URL in data.js"
                />
              )}
            </VideoPlate>
          </motion.div>
        ))}
      </div>

      <div className="text-center mt-10">
        <a
          href={PROFILE.facebook}
          target="_blank"
          rel="noopener noreferrer"
          className="sn-btn sn-btn-ink"
        >
          <Facebook className="w-4 h-4" /> Visit the Facebook page
        </a>
      </div>
    </div>
  </section>
);

/* ===================================================================== */
/* YouTube channel                                                        */
/* ===================================================================== */
/* Accepts watch?v=, youtu.be/, /embed/, /shorts/ and returns an embed URL */
const ytEmbedSrc = (url) => {
  if (!url) return "";
  let id = "";
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) {
      id = u.pathname.slice(1);
    } else if (u.pathname.startsWith("/embed/")) {
      id = u.pathname.split("/embed/")[1];
    } else if (u.pathname.startsWith("/shorts/")) {
      id = u.pathname.split("/shorts/")[1];
    } else {
      id = u.searchParams.get("v") || "";
    }
    id = (id || "").split(/[/?&]/)[0];
  } catch {
    id = url.trim();
  }
  return id ? `https://www.youtube.com/embed/${id}` : "";
};

const YouTubeSection = () => (
  <section id="youtube" className="sn-section sn-bg-paper2">
    <div className="sn-container">
      <motion.div {...revealView} className="text-center max-w-2xl mx-auto mb-12">
        <Kicker center icon={Youtube} ml={MALAYALAM.sections.youtube}>
          Watch · YouTube
        </Kicker>
        <h2 className="sn-title mt-4">
          On the <span className="accent">YouTube</span> channel
        </h2>
        <p className="sn-lead mt-4">
          Longer films, speeches and field reports published on{" "}
          <span className="font-semibold text-ink">{YOUTUBE.channelName}</span>.
        </p>
      </motion.div>

      <div className="grid md:grid-cols-3 gap-8">
        {YOUTUBE_VIDEOS.map((v, i) => {
          const src = ytEmbedSrc(v.url);
          return (
            <motion.div key={i} {...revealView}>
              <VideoPlate caption={v.caption} icon={Youtube}>
                {src ? (
                  <iframe
                    title={v.caption || `YouTube video ${i + 1}`}
                    src={src}
                    className="w-full h-full"
                    style={{ border: "none" }}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                ) : (
                  <PlaceholderCard
                    href={YOUTUBE.channelUrl}
                    label="Add a YouTube video URL in data.js"
                  />
                )}
              </VideoPlate>
            </motion.div>
          );
        })}
      </div>

      <div className="text-center mt-10">
        <a
          href={YOUTUBE.channelUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="sn-btn sn-btn-accent"
        >
          <Youtube className="w-4 h-4" /> Subscribe · Visit the Channel
        </a>
      </div>
    </div>
  </section>
);

/* ===================================================================== */
/* Gallery — photo archive + lightbox                                     */
/* ===================================================================== */
const Gallery = () => {
  const [index, setIndex] = useState(null);
  const gridRef = useMasonry();

  const show = (i) => setIndex(i);
  const close = () => setIndex(null);
  const prev = (e) => {
    e.stopPropagation();
    setIndex((i) => (i - 1 + GALLERY.length) % GALLERY.length);
  };
  const next = (e) => {
    e.stopPropagation();
    setIndex((i) => (i + 1) % GALLERY.length);
  };

  return (
    <section className="sn-section">
      <div className="sn-container">
        <motion.div {...revealView} className="text-center max-w-2xl mx-auto mb-12">
          <Kicker center ml={MALAYALAM.sections.gallery}>Gallery</Kicker>
          <h2 className="sn-title mt-4">
            Moments with the <span className="accent">people</span>
          </h2>
        </motion.div>

        <div className="sn-gallery-masonry" ref={gridRef}>
          {GALLERY.map((g, i) => (
            <motion.button
              key={i}
              {...revealView}
              onClick={() => show(i)}
              className={`sn-figure sn-gallery-item group text-left block w-full ${
                g.featured ? "sn-gallery-item--featured" : ""
              }`}
            >
              <span className="block overflow-hidden">
                <img
                  src={g.image}
                  alt={g.caption}
                  className="w-full h-auto transition-transform duration-500 group-hover:scale-105"
                />
              </span>
              <span className="sn-figure__plate">
                <span className="truncate">{g.caption}</span>
                <span>{String(i + 1).padStart(2, "0")}</span>
              </span>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {index !== null && (
          <motion.div
            className="sn-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
          >
            <button
              onClick={prev}
              className="absolute left-3 md:left-6 text-paper/80 hover:text-saffron-light p-2"
              aria-label="Previous"
            >
              <ChevronLeft className="w-9 h-9" />
            </button>
            <motion.figure
              key={index}
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="max-w-3xl w-full text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={GALLERY[index].image}
                alt={GALLERY[index].caption}
                className="w-full border-2 border-paper"
              />
              <figcaption className="text-paper/85 mt-3 sn-label">
                {GALLERY[index].caption}
              </figcaption>
            </motion.figure>
            <button
              onClick={next}
              className="absolute right-3 md:right-6 text-paper/80 hover:text-saffron-light p-2"
              aria-label="Next"
            >
              <ChevronRight className="w-9 h-9" />
            </button>
            <button
              onClick={close}
              className="absolute top-5 right-5 text-paper/80 hover:text-saffron-light p-2"
              aria-label="Close"
            >
              <X className="w-7 h-7" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

/* ===================================================================== */
/* Contact — reversed green section                                       */
/* ===================================================================== */
const Contact = () => (
  <section id="contact" className="sn-section sn-dark">
    <div className="sn-container grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
      <motion.div {...revealView}>
        <Kicker ml={MALAYALAM.sections.contact}>Get in Touch</Kicker>
        <h2 className="sn-title mt-4 mb-3">
          Let's build a better <span className="accent">community</span>
        </h2>
        <p className="font-mal text-saffron-light text-lg md:text-xl mb-5 leading-snug">
          {MALAYALAM.contactLine}
        </p>
        <p className="sn-lead mb-9 max-w-md">
          For invitations, support requests, or to join his initiatives —
          connect through any of the channels below.
        </p>

        <div className="divide-y divide-paper/15 border-y border-paper/15">
          <a
            href={`tel:${PROFILE.phone.replace(/\s/g, "")}`}
            className="flex items-center gap-4 group py-4"
          >
            <span className="w-11 h-11 rounded-full border border-paper/25 flex items-center justify-center group-hover:bg-saffron group-hover:border-saffron transition-colors">
              <Phone className="w-5 h-5 text-saffron-light group-hover:text-white" />
            </span>
            <span>
              <span className="block sn-label">Phone</span>
              <span className="text-paper font-medium">{PROFILE.phone}</span>
            </span>
          </a>
          <a
            href={`mailto:${PROFILE.email}`}
            className="flex items-center gap-4 group py-4"
          >
            <span className="w-11 h-11 rounded-full border border-paper/25 flex items-center justify-center group-hover:bg-saffron group-hover:border-saffron transition-colors">
              <Mail className="w-5 h-5 text-saffron-light group-hover:text-white" />
            </span>
            <span>
              <span className="block sn-label">Email</span>
              <span className="text-paper font-medium">{PROFILE.email}</span>
            </span>
          </a>
          <div className="flex items-center gap-4 py-4">
            <span className="w-11 h-11 rounded-full border border-paper/25 flex items-center justify-center">
              <MapPin className="w-5 h-5 text-saffron-light" />
            </span>
            <span>
              <span className="block sn-label">Location</span>
              <span className="text-paper font-medium">{PROFILE.location}</span>
            </span>
          </div>
        </div>

        <div className="flex gap-3 mt-8">
          {[
            { icon: Facebook, href: PROFILE.facebook },
            { icon: Youtube, href: PROFILE.youtube },
            { icon: Instagram, href: PROFILE.instagram },
          ].map((s, i) => (
            <a
              key={i}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              className="w-11 h-11 rounded-full border border-paper/25 hover:bg-saffron hover:border-saffron text-paper hover:text-white flex items-center justify-center transition-colors"
            >
              <s.icon className="w-5 h-5" />
            </a>
          ))}
        </div>
      </motion.div>

      <motion.form
        {...revealView}
        onSubmit={(e) => {
          e.preventDefault();
          const name = e.target.name.value;
          const msg = e.target.message.value;
          window.location.href = `mailto:${PROFILE.email}?subject=${encodeURIComponent(
            "Message from " + name
          )}&body=${encodeURIComponent(msg)}`;
        }}
        className="bg-card rounded-sm border border-line p-7"
      >
        <div className="sn-tricolour-chip mb-4" />
        <h3 className="font-display text-2xl font-bold !text-ink mb-1">
          Send a message
        </h3>
        <p className="sn-byline mb-5">We'll get back to you</p>
        <div className="space-y-4">
          <input name="name" required placeholder="Your name" className="sn-input" />
          <input
            name="email"
            type="email"
            required
            placeholder="Your email"
            className="sn-input"
          />
          <textarea
            name="message"
            required
            rows={4}
            placeholder="Your message"
            className="sn-input resize-none"
          />
          <button type="submit" className="sn-btn sn-btn-primary w-full justify-center">
            <Mail className="w-4 h-4" /> Send message
          </button>
        </div>
      </motion.form>
    </div>
  </section>
);

/* ===================================================================== */
/* Footer                                                                 */
/* ===================================================================== */
const Footer = () => (
  <footer className="bg-ink text-paper/80">
    <div className="sn-tricolour" />
    <div className="sn-container py-12">
      <div className="flex flex-col md:flex-row items-center justify-between gap-8">
        <BrandLockup onClick={() => scrollTo("home")} tone="dark" />

        <nav className="flex flex-wrap justify-center gap-5">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => scrollTo(l.id)}
              className="sn-navlink !text-paper/75 hover:!text-saffron-light"
            >
              {l.name}
            </button>
          ))}
        </nav>

        <div className="flex gap-3">
          {[
            { icon: Facebook, href: PROFILE.facebook },
            { icon: Youtube, href: PROFILE.youtube },
            { icon: Instagram, href: PROFILE.instagram },
          ].map((s, i) => (
            <a
              key={i}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 rounded-full border border-paper/20 hover:bg-saffron hover:border-saffron text-paper hover:text-white flex items-center justify-center transition-colors"
            >
              <s.icon className="w-4 h-4" />
            </a>
          ))}
        </div>
      </div>

      <hr className="border-paper/15 mt-10 mb-6" />
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-center">
        <p className="sn-label">
          © {new Date().getFullYear()} {PROFILE.name}. All rights reserved.
        </p>
        <p className="sn-label flex items-center gap-2">
          <span className="font-mal text-saffron-light text-sm normal-case tracking-normal">
            {MALAYALAM.thanks}
          </span>
          <span>·</span>
          <a
            href="https://zedbrand.in"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-saffron-light transition-colors"
          >
            Developed by <span className="underline underline-offset-2">zedbrand.in</span>
          </a>
        </p>
      </div>
    </div>
  </footer>
);

/* ===================================================================== */
/* Scroll-to-top                                                          */
/* ===================================================================== */
const ScrollTop = () => {
  const show = useScrolled(500);
  return (
    <AnimatePresence>
      {show && (
        <motion.button
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.6 }}
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-6 right-6 z-40 w-12 h-12 rounded-full bg-green text-white shadow-lift flex items-center justify-center hover:bg-green-deep transition-colors"
          aria-label="Scroll to top"
        >
          <ArrowUp className="w-5 h-5" />
        </motion.button>
      )}
    </AnimatePresence>
  );
};

/* ===================================================================== */
/* Root                                                                   */
/* ===================================================================== */
const SundaranPortfolio = () => (
  <>
    <Header />
    <Hero />
    <Marquee />
    <Stats />
    <About />
    <Positions />
    <Work />
    <Journey />
    <News />
    <Videos />
    <YouTubeSection />
    <Gallery />
    <Contact />
    <Footer />
    <ScrollTop />
  </>
);

export default SundaranPortfolio;
